import { useCallback, useEffect, useState } from 'react';
import {
  AgxRadio,
  AgxLabel,
  AgxCaption,
  AgxColours,
  CommissionTypes,
} from '@urbanx/agx-ui-components';
import FlatCommission from '../FlatCommission/FlatCommission';
import TieredCommission from '../TieredCommission/TieredCommission';
import VendorChoiceCommission from '../VendorChoiceCommission/VendorChoiceCommission';
import { CommissionDetails } from './types';
import './Commission.scss';

interface CommissionProps {
  id: string;
  commissionTypes: CommissionTypes[];
  onValueChanged: (value: { id: string; value: CommissionDetails }) => void;
  defaultValue?: CommissionDetails;
  showCommissionRangeDisclaimer?: boolean;
  validate?: boolean;
  required?: boolean;
}

const getDefaultCommissionPlan = (defaultVal?: CommissionDetails): CommissionTypes | undefined => {
  if (defaultVal?.flatCommission !== undefined) return CommissionTypes.Flat;
  if (defaultVal?.tieredCommission !== undefined) return CommissionTypes.Tiered;
  if (defaultVal?.vendorChoice !== undefined) return CommissionTypes.VendorChoice;
  return undefined;
};

const Commission = ({
  id,
  commissionTypes,
  onValueChanged,
  defaultValue = undefined,
  showCommissionRangeDisclaimer,
  validate,
  required,
}: CommissionProps) => {
  const [flatCommission, setFlatCommission] = useState(defaultValue?.flatCommission);
  const [tieredCommission, setTieredCommission] = useState(defaultValue?.tieredCommission);
  const [vendorChoiceCommission, setVendorChoiceCommission] = useState(defaultValue?.vendorChoice);

  const [selectedCommissionPlan, setSelectedCommissionPlan] = useState<CommissionTypes | undefined>(
    getDefaultCommissionPlan(defaultValue)
  );

  const getSubContent = (commissionType: CommissionTypes) => {
    if (selectedCommissionPlan !== commissionType) return undefined;
    if (selectedCommissionPlan === CommissionTypes.Flat)
      return [
        <FlatCommission
          id="flat commission"
          key="flat commission"
          onValueChanged={({ value }) => setFlatCommission(value)}
          defaultValue={flatCommission}
          validate={validate}
          required={required}
        />,
      ];
    if (selectedCommissionPlan === CommissionTypes.Tiered)
      return [
        <TieredCommission
          id="tiered commission"
          key="tiered commission"
          onValueChanged={({ value }) => setTieredCommission(value)}
          defaultValue={tieredCommission}
          validate={validate}
          required={required}
        />,
      ];
    if (selectedCommissionPlan === CommissionTypes.VendorChoice)
      return [
        <VendorChoiceCommission
          id="vendorChoice commission"
          key="vendorChoice commission"
          onValueChanged={({ value }) => setVendorChoiceCommission(value)}
          defaultValue={vendorChoiceCommission}
          showCommissionRangeDisclaimer={showCommissionRangeDisclaimer}
          validate={validate}
          required={required}
        />,
      ];

    return undefined;
  };

  const getCommissionLabel = (commissionType: CommissionTypes) => {
    switch (commissionType) {
      case CommissionTypes.Flat:
        return 'Standard';
      case CommissionTypes.Tiered:
        return 'Tiered Commission';
      case CommissionTypes.VendorChoice:
        return 'Vendor Choice';
      default:
        break;
    }
  };

  useEffect(() => {
    let values = {} as CommissionDetails;

    switch (selectedCommissionPlan) {
      case CommissionTypes.Flat:
        values.flatCommission = flatCommission;
        break;
      case CommissionTypes.Tiered:
        values.tieredCommission = tieredCommission;
        break;
      case CommissionTypes.VendorChoice:
        values.vendorChoice = vendorChoiceCommission;
        break;
      default:
        break;
    }

    onValueChanged({ id, value: values });
  }, [selectedCommissionPlan, flatCommission, tieredCommission, vendorChoiceCommission]);

  return (
    <div className="agxRadio">
      <AgxLabel large>Commission Plan</AgxLabel>
      <AgxCaption colour={AgxColours.NEUTRAL_GREY_700}>
        All percentages and figures are inclusive of GST
      </AgxCaption>
      <div className="agxRadio-group">
        {commissionTypes.map((commissionType) => (
          <AgxRadio
            key={commissionType}
            id={commissionType}
            label={getCommissionLabel(commissionType) ?? ''}
            onCheckChanged={() => setSelectedCommissionPlan(commissionType)}
            checked={selectedCommissionPlan === commissionType}
            subContent={getSubContent(commissionType)}
          />
        ))}
      </div>
    </div>
  );
};

export default Commission;
